import React, { useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { isNative } from '../native';
import { Icon } from './ui';
import { callEventAction, CALL_THEME } from '../videoEvents';

// In-app video calls on Daily.co. One private room per pair of users; the
// getVideoRoom Cloud Function checks the two are partners and hands back the
// room plus a meeting token. Any screen opens a call with startCall(); the host
// is mounted once in Dashboard and covers the whole screen until the user leaves.

let open = null;
export const startCall = (partnerUid, partnerName) => open?.({ partnerUid, partnerName, key: Date.now() });

export function VideoCallHost() {
  const [call, setCall] = useState(null);
  useEffect(() => { open = setCall; return () => { open = null; }; }, []);
  return call ? <VideoCall key={call.key} {...call} onClose={() => setCall(null)} /> : null;
}

// The function's own message for refusals (not partners yet, signed out);
// a plain one for anything else.
const callError = (e) => {
  const code = String(e?.code || '').replace('functions/', '');
  if (['permission-denied', 'unauthenticated', 'invalid-argument', 'unavailable'].includes(code) && e.message) return e.message;
  return 'Could not open the video room. Check your connection and try again.';
};

const CAMERA_MESSAGE = 'MedXMeet can\'t use your camera or microphone. Allow them for this site (on iPhone: Settings → MedXMeet), then try again.';

const keepAwake = async (on) => {
  if (!isNative) return;
  try {
    const { KeepAwake } = await import('@capacitor-community/keep-awake');
    await (on ? KeepAwake.keepAwake() : KeepAwake.allowSleep());
  } catch { /* the call works without it */ }
};

function VideoCall({ partnerUid, partnerName, onClose }) {
  const frameHost = useRef(null);
  const [error, setError] = useState(null);
  const [cameraBlocked, setCameraBlocked] = useState(false);
  const [joined, setJoined] = useState(false);
  // Bumped by "Try again" to start the room over.
  const [attempt, setAttempt] = useState(0);
  // Dashboard redraws often; the call must not restart when onClose changes identity.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    let frame = null;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await httpsCallable(functions, 'getVideoRoom')({ partnerUid });
        const Daily = (await import('@daily-co/daily-js')).default;
        if (cancelled) return;
        frame = Daily.createFrame(frameHost.current, {
          showLeaveButton: true,
          showFullscreenButton: false,
          theme: CALL_THEME,
          iframeStyle: { width: '100%', height: '100%', border: '0' },
        });
        frame.on('joined-meeting', () => setJoined(true));
        const onEvent = ev => {
          const act = callEventAction(ev);
          if (act === 'close') closeRef.current();
          else if (act === 'camera') setCameraBlocked(true);
        };
        ['left-meeting', 'error', 'camera-error'].forEach(name => frame.on(name, onEvent));
        keepAwake(true);
        await frame.join({ url: data.url, token: data.token });
      } catch (e) {
        if (!cancelled) setError(callError(e));
      }
    })();
    return () => {
      cancelled = true;
      keepAwake(false);
      frame?.destroy();
    };
  }, [partnerUid, attempt]);

  const retry = () => { setCameraBlocked(false); setError(null); setJoined(false); setAttempt(a => a + 1); };

  // The bar (with Close) stays above the call in every state, so there is always a
  // way back. Escape presses it too: Dashboard clicks the last .modal-close.
  return (
    <div className="video-call" role="dialog" aria-modal="true" aria-label={`Video call with ${partnerName}`}>
      <div className="video-call-bar">
        <span className="video-call-title">Video call with {partnerName.trim()}</span>
        <button className="btn-icon small plain video-call-close modal-close" onClick={onClose} aria-label="Close video call"><Icon name="close" size={20} /></button>
      </div>
      <div className="video-call-body">
        {error || cameraBlocked ? (
          <div className="video-call-message">
            <p>{error || CAMERA_MESSAGE}</p>
            <div className="video-call-actions">
              <button className="btn" onClick={retry}>Try again</button>
              <button className="btn secondary" onClick={onClose}>Back</button>
            </div>
          </div>
        ) : (
          <>
            {!joined && <div className="video-call-message"><div className="loading-spinner" /><p>Opening your room…</p></div>}
            <div key={attempt} className="video-call-frame" ref={frameHost} />
          </>
        )}
      </div>
    </div>
  );
}
